import React, { useState, useEffect, useCallback } from "react";
import { useApiClient } from "../../../data/clients/ApiClient";
import { useDesignRepository } from "../../../data/repositories/DesignRepository";
import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { BoxHeader2 } from "../../components/BoxView/BoxHeader2";
import { BoxHeaderTypography } from "../../components/Typographies/BoxHeaderTypography";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { PrimaryButton } from "../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../components/Buttons/SecondaryButton";
import { useSuccessToast, useErrorToast } from "../../components/Toast/Toast";
import LoadingElement from "../../components/loading-element/loading-element";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faSearch,
  faFilter,
  faCheck,
  faCheckDouble,
  faExclamationTriangle,
  faRotate,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { useConfirmationDialog } from "../../components/Dialogs/confirmation-dialog";
import { PageQuery } from "../../../utils/models/pageQuery";
import { SortDirection } from "../../components/GenericTable/Models/sortDirection";
import { DesignImage } from "../../components/Design/DesignImage";
import {
  Checkbox,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
} from "@mui/material";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { useCustomerAndVendors } from "../../shared/hooks/useCustomerAndVendors";

const PAGE_SIZE = 50;

export function DesignImportScreen() {
  const apiClient = useApiClient();
  const { organizationRepository } = useRepositoriesContext();
  const designRepository = useDesignRepository();

  // Input filter states
  const [searchTermInput, setSearchTermInput] = useState("");
  const [organizationSearchInput, setOrganizationSearchInput] = useState("");
  const [customerIdInput, setCustomerIdInput] = useState<number | "">("");

  // Applied filter states
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [appliedOrganizationSearch, setAppliedOrganizationSearch] =
    useState("");
  const [appliedCustomerId, setAppliedCustomerId] = useState<number | "">("");
  const [pageIndex, setPageIndex] = useState(1);

  // Data states
  const [selectedDesigns, setSelectedDesigns] = useState<Set<string>>(
    new Set()
  );
  const [isImporting, setIsImporting] = useState(false);
  const [isLoadingAllSerials, setIsLoadingAllSerials] = useState(false);
  const [totalMatchingCount, setTotalMatchingCount] = useState<number | null>(
    null
  );
  const [importResult, setImportResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Apply filters
  const handleSearch = () => {
    setAppliedSearchTerm(searchTermInput);
    setAppliedOrganizationSearch(organizationSearchInput);
    setAppliedCustomerId(customerIdInput);
    setPageIndex(1);
  };

  // Clear filters
  const handleClearFilters = () => {
    setSearchTermInput("");
    setOrganizationSearchInput("");
    setCustomerIdInput("");
    setAppliedSearchTerm("");
    setAppliedOrganizationSearch("");
    setAppliedCustomerId("");
    setPageIndex(1);
    setTotalMatchingCount(null);
  };

  // Select all matching
  const handleSelectAllMatching = async () => {
    setIsLoadingAllSerials(true);
    try {
      const response = await apiClient.getDesignSerialsForImport({
        searchTerm: appliedSearchTerm || undefined,
        organizationName: appliedOrganizationSearch || undefined,
        customerId: appliedCustomerId || undefined,
      });

      if (response.serials && response.serials.length > 0) {
        const newSelection = new Set(response.serials);
        setSelectedDesigns(newSelection);
        setTotalMatchingCount(response.count);
        useSuccessToast(`${response.count} designs valgt`);
      } else {
        useErrorToast("Ingen designs fundet med de valgte filtre");
      }
    } catch (error) {
      console.error("Error fetching all serials:", error);
      useErrorToast("Fejl ved hentning af designs");
    } finally {
      setIsLoadingAllSerials(false);
    }
  };

  // Build query
  const buildQuery = useCallback((): PageQuery => {
    const filters: { field: string; value: string }[] = [];

    if (appliedCustomerId) {
      filters.push({ field: "customerId", value: String(appliedCustomerId) });
    }
    if (appliedOrganizationSearch.trim()) {
      filters.push({
        field: "organizationName",
        value: appliedOrganizationSearch.trim(),
      });
    }

    return {
      searchTerm: appliedSearchTerm,
      filterBy: filters,
      sortBy: { field: "createdAtUtc", sortDirection: SortDirection.DESC },
      page: { size: PAGE_SIZE, index: pageIndex },
    };
  }, [
    appliedSearchTerm,
    appliedOrganizationSearch,
    appliedCustomerId,
    pageIndex,
  ]);

  // Fetch designs
  const query = React.useMemo(() => buildQuery(), [buildQuery]);
  const {
    data: designData,
    isLoading,
    isError,
  } = designRepository.useAllDesigns(query);

  // Get customers
  const { customers, isLoading: isLoadingCustomers } = useCustomerAndVendors(
    organizationRepository
  );

  // Queue counts
  const [queueCounts, setQueueCounts] = useState<
    { queueName: string; count: number }[]
  >([]);

  const fetchQueueCounts = async () => {
    try {
      const counts = await designRepository.getImportDesignsQueueCounts();
      setQueueCounts(counts);
    } catch (error) {
      console.error("Error fetching queue counts:", error);
    }
  };

  useEffect(() => {
    fetchQueueCounts();
    const interval = setInterval(fetchQueueCounts, 10000);
    return () => clearInterval(interval);
  }, []);

  // Handle select all on page
  const handleSelectAll = () => {
    if (!designData?.results) return;

    const currentPageSerials = designData.results
      .filter((design) => design.product?.mul)
      .map((design) => design.product!.mul);

    const allSelected = currentPageSerials.every((sku) =>
      selectedDesigns.has(sku)
    );

    if (allSelected) {
      const newSelection = new Set(selectedDesigns);
      currentPageSerials.forEach((sku) => newSelection.delete(sku));
      setSelectedDesigns(newSelection);
    } else {
      const newSelection = new Set(selectedDesigns);
      currentPageSerials.forEach((sku) => newSelection.add(sku));
      setSelectedDesigns(newSelection);
    }
  };

  // Handle individual selection
  const handleDesignSelect = (sku: string) => {
    const newSelection = new Set(selectedDesigns);
    if (newSelection.has(sku)) {
      newSelection.delete(sku);
    } else {
      newSelection.add(sku);
    }
    setSelectedDesigns(newSelection);
  };

  // Handle import
  const handleImport = async () => {
    if (selectedDesigns.size === 0) {
      useErrorToast("Vælg mindst ét design at importere");
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const serialNumbers = Array.from(selectedDesigns);
      const result = await apiClient.bulkDesignImport(serialNumbers);

      if (result.success) {
        useSuccessToast(`${result.queuedCount} designs sat i import-kø`);
        setImportResult({ success: true, message: result.message });
        setSelectedDesigns(new Set());
        fetchQueueCounts();
      } else {
        useErrorToast("Import fejlede");
        setImportResult({ success: false, message: "Import fejlede" });
      }
    } catch (error) {
      console.error("Import error:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Der opstod en fejl under import";
      useErrorToast(errorMessage);
      setImportResult({ success: false, message: errorMessage });
    } finally {
      setIsImporting(false);
    }
  };

  // Confirmation dialog
  const confirmationDialog = useConfirmationDialog(
    `Er du sikker på, at du vil starte import af ${selectedDesigns.size} designs?\n\nDette vil opdatere data fra Navision for de valgte designs.`,
    handleImport
  );

  const allOnPageSelected =
    designData?.results
      ?.filter((design) => design.product?.mul)
      ?.every((design) => selectedDesigns.has(design.product!.mul)) ?? false;

  const importQueueCount =
    queueCounts.find((q) => q.queueName === "import-designs-queue")?.count ?? 0;

  return (
    <div className="p-8 space-y-6">
      {confirmationDialog.component}

      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-800 mb-2">
          Admin: Design Import
        </h1>
        <p className="text-sm text-gray-600">
          Vælg designs der skal genimporteres fra Navision. Du kan filtrere på
          kunde, organisationsnavn eller søge efter specifikke designs.
        </p>
      </div>

      {/* Queue Status */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faUpload} className="text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Import kø:</span>
          <span
            className={`text-sm font-bold ${
              importQueueCount > 0 ? "text-amber-600" : "text-green-600"
            }`}
          >
            {importQueueCount} ventende
          </span>
        </div>
        <SecondaryButton width="sm" onClick={fetchQueueCounts}>
          <FontAwesomeIcon icon={faRotate} className="mr-2" />
          Opdater
        </SecondaryButton>
      </div>

      {/* Filters Section */}
      <JetSportBox>
        <BoxHeader2>
          <BoxHeaderTypography>
            <FontAwesomeIcon icon={faFilter} className="mr-2" />
            Filtre
          </BoxHeaderTypography>
        </BoxHeader2>
        <BoxContent className="!min-h-0 py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Search */}
            <TextField
              label="Søg (serienummer, titel)"
              variant="outlined"
              size="small"
              value={searchTermInput}
              onChange={(e) => setSearchTermInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                startAdornment: (
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="mr-2 text-gray-400"
                  />
                ),
              }}
            />

            {/* Organization Name Search */}
            <TextField
              label="Organisation navn indeholder..."
              variant="outlined"
              size="small"
              value={organizationSearchInput}
              onChange={(e) => setOrganizationSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder='F.eks. "Sport 24"'
              InputProps={{
                startAdornment: (
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="mr-2 text-gray-400"
                  />
                ),
              }}
            />

            {/* Customer */}
            <FormControl size="small">
              <InputLabel>Kunde</InputLabel>
              <Select
                value={customerIdInput}
                label="Kunde"
                onChange={(e) =>
                  setCustomerIdInput(e.target.value as number | "")
                }
                disabled={isLoadingCustomers}
              >
                <MenuItem value="">Alle kunder</MenuItem>
                {customers?.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="flex gap-4">
            <PrimaryButton onClick={handleSearch}>
              <FontAwesomeIcon icon={faSearch} className="mr-2" />
              Søg
            </PrimaryButton>
            <SecondaryButton width="sm" onClick={handleClearFilters}>
              Ryd filtre
            </SecondaryButton>
          </div>
        </BoxContent>
      </JetSportBox>

      {/* Selection Summary & Import Button */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm font-medium text-blue-800">
            {selectedDesigns.size} designs valgt
            {totalMatchingCount !== null &&
              totalMatchingCount !== selectedDesigns.size && (
                <span className="text-blue-600">
                  {" "}
                  (af {totalMatchingCount} matchende)
                </span>
              )}
          </span>

          <PrimaryButton
            onClick={handleSelectAllMatching}
            disabled={isLoadingAllSerials}
          >
            {isLoadingAllSerials ? (
              <>
                <FontAwesomeIcon
                  icon={faRotate}
                  className="mr-2 animate-spin"
                />
                Henter...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheckDouble} className="mr-2" />
                Vælg alle matchende
              </>
            )}
          </PrimaryButton>

          {selectedDesigns.size > 0 && (
            <SecondaryButton
              width="sm"
              onClick={() => setSelectedDesigns(new Set())}
            >
              Ryd valg
            </SecondaryButton>
          )}
        </div>
        <div className="flex gap-2">
          <PrimaryButton
            onClick={confirmationDialog.open}
            disabled={selectedDesigns.size === 0 || isImporting}
          >
            {isImporting ? (
              <>
                <FontAwesomeIcon
                  icon={faRotate}
                  className="mr-2 animate-spin"
                />
                Importerer...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlay} className="mr-2" />
                Start Import ({selectedDesigns.size})
              </>
            )}
          </PrimaryButton>
        </div>
      </div>

      {/* Import Result */}
      {importResult && (
        <div
          className={`border rounded-lg p-4 flex items-center gap-3 ${
            importResult.success
              ? "bg-green-50 border-green-200"
              : "bg-red-50 border-red-200"
          }`}
        >
          <FontAwesomeIcon
            icon={importResult.success ? faCheck : faExclamationTriangle}
            className={importResult.success ? "text-green-500" : "text-red-500"}
          />
          <span
            className={importResult.success ? "text-green-700" : "text-red-700"}
          >
            {importResult.message}
          </span>
        </div>
      )}

      {/* Designs Table */}
      <JetSportBox>
        <BoxHeader2>
          <div className="flex items-center justify-between w-full">
            <BoxHeaderTypography>
              Designs ({designData?.results?.length ?? 0} på denne side, side{" "}
              {designData?.pageIndex ?? 1} af {designData?.pageCount ?? 1})
            </BoxHeaderTypography>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={
                  allOnPageSelected &&
                  designData?.results &&
                  designData.results.length > 0
                }
                indeterminate={selectedDesigns.size > 0 && !allOnPageSelected}
                onChange={handleSelectAll}
                size="small"
              />
              <span className="text-sm text-gray-600">Vælg alle på siden</span>
            </div>
          </div>
        </BoxHeader2>
        <BoxContent className="!min-h-0 py-0 px-0">
          {isLoading ? (
            <div className="h-64 relative">
              <LoadingElement />
            </div>
          ) : isError ? (
            <div className="p-8 text-center text-red-500">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              Fejl ved hentning af designs
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="py-3 px-4 text-left w-12"></th>
                      <th className="py-3 px-4 text-left w-20">Billede</th>
                      <th className="py-3 px-4 text-left">Serienummer</th>
                      <th className="py-3 px-4 text-left">Titel</th>
                      <th className="py-3 px-4 text-left">Kunde</th>
                      <th className="py-3 px-4 text-left">Style</th>
                    </tr>
                  </thead>
                  <tbody>
                    {designData?.results?.map((design) => {
                      const sku = design.product?.mul;
                      const canSelect = !!sku;
                      const isSelected = sku ? selectedDesigns.has(sku) : false;

                      return (
                        <tr
                          key={design.id}
                          className={`border-b hover:bg-gray-50 ${
                            isSelected ? "bg-blue-50" : ""
                          } ${!canSelect ? "opacity-50" : "cursor-pointer"}`}
                          onClick={() => canSelect && handleDesignSelect(sku!)}
                        >
                          <td className="py-2 px-4">
                            {canSelect && (
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleDesignSelect(sku!)}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                              />
                            )}
                          </td>
                          <td className="py-2 px-4">
                            <div className="w-12 h-12 flex items-center justify-center">
                              {design.displayImage && (
                                <DesignImage
                                  design={design}
                                  className="max-w-12 max-h-12 object-contain"
                                />
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-4 font-mono text-xs">
                            {sku || "-"}
                          </td>
                          <td className="py-2 px-4">{design.title || "-"}</td>
                          <td className="py-2 px-4 text-xs">
                            {design.customer?.name || "-"}
                          </td>
                          <td className="py-2 px-4 text-xs">
                            {design.style?.name || "-"}
                          </td>
                        </tr>
                      );
                    })}
                    {(!designData?.results ||
                      designData.results.length === 0) && (
                      <tr>
                        <td
                          colSpan={6}
                          className="py-8 text-center text-gray-500"
                        >
                          Ingen designs fundet med de valgte filtre
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {designData && designData.pageCount > 1 && (
                <div className="py-4 px-4 flex justify-center border-t">
                  <Pagination
                    count={designData.pageCount}
                    page={pageIndex}
                    onChange={(_, page) => setPageIndex(page)}
                    color="primary"
                  />
                </div>
              )}
            </>
          )}
        </BoxContent>
      </JetSportBox>
    </div>
  );
}

export default DesignImportScreen;
